// Mock da Cotação Fase 2 — dirige TODO o funil sem backend (localStorage).
// Atrás de FEATURE_FLAGS.COTACAO_FASE2. Quando o backend existir, estas funções
// viram thunks em Store/Actions/Quote.js (mesmos nomes/semântica).
// Shape do item espelha o normalizeItem real (periodo/pax/hotel/quarto/precoUSD/precoBRL).
// Spec: .context/specs/cotacao-fase2-funil-cliente.md

import { agruparTrechos, QUOTE_STATUS_F2, derivarStatusPorReservas } from "../Constants/quote";

const LS_KEY = "@dream4/mock/cotacao-fase2";
const CAMBIO = { usdBrl: 5.42, data: "2026-07-11" };

const brl = (usd) => Math.round(usd * CAMBIO.usdBrl * 100) / 100;

// --- Seed: um roteiro Rio + Búzios (1 trecho obrigatório, 1 opcional) --------
const seed = () => {
	const mk = (id, trechoId, hotel, cidade, foto, ci, co, quartoNome, regime, cafe, usd) => ({
		id,
		trechoId,
		periodo: { checkIn: ci, checkOut: co, noites: 3 },
		pax: { adultos: 2, criancas: 0 },
		hotel: { id: hotel.toLowerCase().replace(/\s/g, ""), nome: hotel, cidade, pais: "Brasil", foto },
		quarto: { nome: quartoNome, tarifa: `${quartoNome}-RATE`, regime, cafe, roomId: `${id}-room`, ratePlanCode: `${id}-rp` },
		temCafe: cafe,
		precoUSD: usd,
		precoBRL: brl(usd),
		disponivel: true,
		hotelViewToken: `htok-${id}`,
	});
	const foto1 = "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600";
	const foto2 = "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=600";
	const foto3 = "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600";
	return [
		{
			id: "q-demo-1",
			token: "demo",
			nome: "Lua de mel — Rio + Búzios",
			status: QUOTE_STATUS_F2.RASCUNHO,
			createdBy: { nome: "Ana (Agência Viva)", agencyName: "Viva Turismo" },
			criadoEm: "2026-07-11T12:00:00.000Z",
			atualizadoEm: "2026-07-11T12:00:00.000Z",
			contato: { nome: "", email: "", documento: "", telefone: "", isForeigner: false },
			cambio: CAMBIO,
			visualizadoEm: null,
			aprovadaEm: null,
			enviadaEm: null,
			selecaoCliente: {},
			perda: null,
			reservas: [],
			eventos: [{ tipo: "criada", detalhe: "Cotação criada", em: "2026-07-11T12:00:00.000Z" }],
			trechos: [
				{ id: "t1", label: "Rio de Janeiro — 3 noites", obrigatorio: true, periodoLabel: "10–13 ago 2026" },
				{ id: "t2", label: "Búzios — 3 noites", obrigatorio: false, periodoLabel: "13–16 ago 2026" },
			],
			itens: [
				mk("i1", "t1", "Copacabana Palace", "Rio de Janeiro", foto1, "2026-08-10", "2026-08-13", "Superior Vista Mar", "Café da manhã", true, 1450),
				mk("i2", "t1", "Copacabana Palace", "Rio de Janeiro", foto1, "2026-08-10", "2026-08-13", "Deluxe Ocean Front", "Café da manhã", true, 2100),
				mk("i3", "t2", "Insólito Boutique", "Búzios", foto2, "2026-08-13", "2026-08-16", "Bangalô Jardim", "Meia pensão", true, 980),
				mk("i4", "t2", "Abracadabra Pousada", "Búzios", foto3, "2026-08-13", "2026-08-16", "Suíte Pôr do Sol", "Café da manhã", true, 760),
			],
		},
	];
};

// --- Persistência ----------------------------------------------------------
const load = () => {
	try {
		const raw = localStorage.getItem(LS_KEY);
		if (raw) return JSON.parse(raw);
	} catch (_) {}
	const s = seed();
	save(s);
	return s;
};
const save = (quotes) => {
	try { localStorage.setItem(LS_KEY, JSON.stringify(quotes)); } catch (_) {}
	try { window.dispatchEvent(new Event("dream4:cotacao-fase2-changed")); } catch (_) {}
};

export const resetMockFase2 = () => { const s = seed(); save(s); return s; };

// --- Leitura ---------------------------------------------------------------
export const listMockQuotes = () => load();
export const getMockQuoteById = (id) => load().find((q) => q.id === id) || null;
export const getMockQuoteByToken = (token) => load().find((q) => q.token === token) || null;

const mutate = (id, fn) => {
	const quotes = load();
	const i = quotes.findIndex((q) => q.id === id);
	if (i < 0) return null;
	quotes[i] = fn({ ...quotes[i] }) || quotes[i];
	save(quotes);
	return quotes[i];
};
const mutateByToken = (token, fn) => {
	const q = getMockQuoteByToken(token);
	return q ? mutate(q.id, fn) : null;
};

// Anexa um evento ao histórico da cotação (item 4 — trilha de auditoria leve).
// tipo dirige o ícone/label na UI; detalhe é o texto livre (ex.: destinatário do e-mail).
const withEvento = (q, tipo, detalhe) => ({
	...q,
	eventos: [...(q.eventos || []), { tipo, detalhe: detalhe || "", em: new Date().toISOString() }],
});

// --- Transições (agência) --------------------------------------------------
export const enviarParaSelecao = (id, contato) =>
	mutate(id, (q) => withEvento(
		{ ...q, status: QUOTE_STATUS_F2.AGUARDANDO_SELECAO, contato, enviadaEm: new Date().toISOString() },
		"enviada", `E-mail com o link enviado para ${contato?.nome || "cliente"}${contato?.email ? ` <${contato.email}>` : ""}`,
	));

// Item 3 — avança para seleção SEM disparar e-mail (exceção: a agência vai
// operar como cliente / selecionar em nome dele). Não sobrescreve o contato.
export const avancarSemEnviar = (id) =>
	mutate(id, (q) => withEvento(
		{ ...q, status: QUOTE_STATUS_F2.AGUARDANDO_SELECAO, enviadaEm: new Date().toISOString() },
		"avancada_sem_envio", "Avançada para seleção sem envio de e-mail (agência opera pelo cliente)",
	));

// Item 9 — reenvia o e-mail, podendo manter ou alterar o destinatário.
export const reenviarEmail = (id, email) =>
	mutate(id, (q) => withEvento(
		{ ...q, contato: { ...(q.contato || {}), ...(email ? { email } : {}) } },
		"reenviada", `E-mail reenviado para ${email || q.contato?.email || "cliente"}`,
	));

export const desfazerEnvio = (id) =>
	mutate(id, (q) => withEvento(
		{ ...q, status: QUOTE_STATUS_F2.RASCUNHO, visualizadoEm: null },
		"desfeito", "Envio desfeito — link desativado, cliente notificado",
	));

// Autoria (rascunho): agência marca o trecho como obrigatório/opcional.
export const setTrechoObrigatorio = (id, trechoId, obrigatorio) =>
	mutate(id, (q) => ({ ...q, trechos: (q.trechos || []).map((t) => (t.id === trechoId ? { ...t, obrigatorio } : t)) }));

export const reabrirParaAjuste = (id, destino /* 'aguardando_selecao' | 'rascunho' */) =>
	mutate(id, (q) => withEvento({
		...q,
		status: destino === QUOTE_STATUS_F2.RASCUNHO ? QUOTE_STATUS_F2.RASCUNHO : QUOTE_STATUS_F2.AGUARDANDO_SELECAO,
		aprovadaEm: null,
		...(destino === QUOTE_STATUS_F2.RASCUNHO ? { selecaoCliente: {} } : {}),
	}, "reaberta", destino === QUOTE_STATUS_F2.RASCUNHO ? "Reaberta para remontar opções (rascunho)" : "Reaberta para nova seleção — cliente notificado"));

export const marcarPerdida = (id, motivo) =>
	mutate(id, (q) => withEvento(
		{ ...q, status: QUOTE_STATUS_F2.PERDIDA, perda: { motivo, estagio: q.status, em: new Date().toISOString() } },
		"perdida", `Marcada como perdida (${motivo})`,
	));

export const reabrirPerdida = (id) =>
	mutate(id, (q) => withEvento({ ...q, status: QUOTE_STATUS_F2.RASCUNHO, perda: null }, "reaberta", "Cotação perdida reaberta (rascunho)"));

// Rascunho: remove uma opção (item) da cotação.
export const removerItem = (id, itemId) =>
	mutate(id, (q) => {
		const alvo = (q.itens || []).find((it) => it.id === itemId);
		const rotulo = alvo ? `${alvo.hotel?.nome || "opção"} — ${alvo.quarto?.nome || ""}` : "opção";
		return withEvento({ ...q, itens: (q.itens || []).filter((it) => it.id !== itemId) }, "item_removido", `Opção removida: ${rotulo}`);
	});

// Rascunho: exclui a cotação inteira (remove da lista).
export const excluirCotacao = (id) => {
	const quotes = load().filter((q) => q.id !== id);
	save(quotes);
	return true;
};

// Re-cota (mock): simula a atualização de disponibilidade/preços/câmbio ao abrir.
// O câmbio Dream4 é diário (não muda intraday), então os valores ficam estáveis;
// aqui só marca o refresh. Na API real, reusa /quote/refresh.
export const recotar = (id) =>
	mutate(id, (q) => ({ ...q, atualizadoEm: new Date().toISOString() }));

// Consulta de documento (MOCK) — mesma dinâmica do fluxo de reserva real
// (Store/Actions/Order.js → retrievePersonInfoByDocument): CPF válido resolve
// nome + nascimento; senão libera edição manual. Trocar pela action real na
// integração. Retorna { success, personData:{nome, nascimento(YYYY-MM-DD)} }.
export const consultarDocumentoMock = (cpf) =>
	new Promise((resolve) => {
		const digits = String(cpf || "").replace(/\D/g, "");
		setTimeout(() => {
			if (digits.length !== 11) { resolve({ success: false, errorMessage: "CPF inválido" }); return; }
			const nomes = ["Marina Souza Lima", "Carlos Eduardo Farias", "Beatriz Nunes Rocha", "Rafael Andrade", "Juliana Prado", "Thiago Mendes"];
			const nome = nomes[Number(digits[0]) % nomes.length];
			const ano = 1970 + (Number(digits.slice(0, 2)) % 35);
			const mes = String((Number(digits[2]) % 12) + 1).padStart(2, "0");
			const dia = String((Number(digits[3]) % 27) + 1).padStart(2, "0");
			resolve({ success: true, personData: { nome, nascimento: `${ano}-${mes}-${dia}` } });
		}, 600);
	});

// Prazo de pagamento (mock): hoje + 3 dias (o real vem do backend/módulo de reservas).
const prazoPagamentoMock = () => {
	const d = new Date();
	d.setDate(d.getDate() + 3);
	return d.toISOString().slice(0, 10);
};

// Cria as reservas (mock) a partir da seleção aprovada + dados de responsável/hóspedes.
// Move para RESERVADA. `dados` = { responsavel, hospedesPorReserva }.
export const prosseguirReserva = (id, dados = {}) =>
	mutate(id, (q) => {
		const sel = q.selecaoCliente || {};
		const prazo = prazoPagamentoMock();
		const reservas = Object.entries(sel)
			.filter(([, itemId]) => !!itemId)
			.map(([trechoId, itemId]) => {
				const it = q.itens.find((x) => x.id === itemId);
				return {
					id: `r-${trechoId}`,
					trechoId,
					status: "pendente", // aguardando pagamento (baixa vem do módulo de reservas)
					// Código de confirmação (mock — o real vem do createBooking do backend).
					codigoReserva: `D4-${String(it?.hotel?.id || "HOTEL").replace(/[^a-z0-9]/gi, "").slice(0, 5).toUpperCase()}-${String(trechoId).toUpperCase()}`,
					hotel: it?.hotel?.nome,
					foto: it?.hotel?.foto || null,
					regime: it?.quarto?.regime || null,
					temCafe: it?.temCafe || false,
					periodo: it?.periodo,
					quarto: it?.quarto?.nome,
					precoUSD: it?.precoUSD,
					precoBRL: it?.precoBRL,
					prazoPagamento: prazo,
					hotelViewToken: it?.hotelViewToken || null,
					hospedes: (dados.hospedesPorReserva && dados.hospedesPorReserva[trechoId]) || [],
					solicitacaoEspecial: (dados.solicitacoesPorReserva && dados.solicitacoesPorReserva[trechoId]) || "",
				};
			});
		return withEvento({ ...q, status: QUOTE_STATUS_F2.RESERVADA, reservas, responsavel: dados.responsavel || null, responsavelEhHospede: dados.responsavelEhHospede !== false }, "reservada", `${reservas.length} reserva(s) criada(s) — aguardando pagamento`);
	});

// Demo: alterna o pagamento de uma reserva e re-deriva o status da cotação.
export const setReservaStatus = (id, reservaId, status) =>
	mutate(id, (q) => {
		const reservas = (q.reservas || []).map((r) => (r.id === reservaId ? { ...r, status } : r));
		const hotelNome = (q.reservas || []).find((r) => r.id === reservaId)?.hotel || "reserva";
		return withEvento(
			{ ...q, reservas, status: derivarStatusPorReservas(reservas) },
			status === "paga" ? "pagamento" : "cancelamento",
			status === "paga" ? `Pagamento confirmado — ${hotelNome}` : `Reserva cancelada — ${hotelNome}`,
		);
	});

// --- Transições (cliente / público) ----------------------------------------
export const registrarVisualizacao = (token) =>
	mutateByToken(token, (q) => (q.visualizadoEm ? q : withEvento({ ...q, visualizadoEm: new Date().toISOString() }, "visualizada", "Cliente abriu o link")));

// Aprovação atômica: cliente manda a seleção e aprova de uma vez.
// `por` = 'cliente' (link público) | 'agência' (SUDO, operar como cliente).
export const aprovarSelecao = (token, selecao, por = "cliente") =>
	mutateByToken(token, (q) => withEvento({
		...q,
		status: QUOTE_STATUS_F2.APROVADA,
		selecaoCliente: selecao,
		aprovadaEm: new Date().toISOString(),
	}, "aprovada", por === "agência" ? "Aprovada pela agência (operando como cliente)" : "Aprovada pelo cliente"));

// Cliente desfaz a própria aprovação para reajustar a seleção (volta o link a
// ficar editável). Mantém a seleção anterior pré-preenchida. Notifica a agência.
export const clienteRefazerSelecao = (token) =>
	mutateByToken(token, (q) => (
		q.status === QUOTE_STATUS_F2.APROVADA
			? withEvento({ ...q, status: QUOTE_STATUS_F2.AGUARDANDO_SELECAO, aprovadaEm: null }, "reaberta_cliente", "Cliente reabriu para ajustar a seleção")
			: q
	));

// ---------------------------------------------------------------------------
// PREVIEW DE VALORES (mock) — "ver valores de hoje" sem alterar a cotação
// ---------------------------------------------------------------------------
// Enquanto o BE não entrega `POST /api/v1/quote/:id/preview-values`. Contrato em
// .context/api-contracts/FRONTEND-preview-de-valores-2026-09-04.md
//
// O mock simula os TRÊS desfechos que a tela precisa saber tratar, porque com todos os itens
// iguais a UI nunca é exercitada: um item que só variou por câmbio, um cuja TARIFA subiu, e um
// que ficou INDISPONÍVEL. É determinístico (varia pelo índice, não por sorteio) para o
// comportamento não mudar entre dois cliques.
//
// Modo `reservada`: a tarifa está travada no fornecedor, então só o câmbio move — nenhum item
// muda de USD e nenhum fica indisponível.
// Recebe a COTACAO, nao o id. Antes buscava em `load()` (localStorage), que so tem as cotacoes
// semeadas do mock — com a API real ligada a cotacao aberta NAO esta la, e o mock devolvia
// `{itens: []}` EM SILENCIO. A tela entao classificava todo item como "nao consultado" e aquilo
// parecia falha do fornecedor: o owner quase abriu chamado no BE por causa disso (05/09).
export const previewValores = (quote) => {
	const q = quote && quote.id ? quote : null;
	if (!q) return { itens: [], cambio: null };

	const sel = q.selecaoCliente || {};
	// `opcoes` NAO existe em `quote.trechos` — quem monta e o agruparTrechos, a partir de
	// `quote.itens`. A versao anterior lia `t.opcoes` direto e so funcionava com o shape semeado do
	// mock; com a cotacao real vinha vazio e a tela dizia que a consulta nao retornou nada.
	// Usar a MESMA funcao que o componente usa e o que garante que os ids batem.
	const selecionados = agruparTrechos(q)
		.map((t) => (t.opcoes || []).find((o) => o.id === sel[t.id]))
		.filter(Boolean);

	// O BE removeu o modo `cambio` em 05/09: em `reservada` a rota devolve 409, porque a tarifa esta
	// travada e o cambio ja e recomputado no retrieve — o preview daria o numero que a tela ja mostra.
	// Encerradas tambem recusam. Sobra um regime so, e o mock reflete isso.

	// Câmbio "de hoje" ~4,3% acima do da cotação, com a janela real que o BE passou a mandar.
	const base = Number(q.cambio?.usdBrl) || 5.2;
	const usdBrl = Math.round(base * 1.043 * 100) / 100;
	const agora = new Date();
	const validoAte = new Date(agora.getTime() + 6 * 60 * 60 * 1000);

	const itens = selecionados.map((it, i) => {
		const precoUSD = Number(it.precoUSD) || 0;
		// Os quatro desfechos que a tela precisa distinguir (contrato do BE, 04/09): o item some; a
		// tarifa existe mas a politica de cancelamento recusa; o fornecedor nao responde ("nao sei",
		// nao "sumiu"); e o caso normal.
		if (i % 4 === 2) return { id: it.id, precoUSD: null, precoBRL: null, disponivel: false, motivo: "indisponivel" };
		if (i % 4 === 3) return { id: it.id, precoUSD: null, precoBRL: null, disponivel: false, motivo: "FREE_WINDOW_TOO_SHORT" };
		const novoUSD = i % 4 === 1 ? Math.round(precoUSD * 1.15 * 100) / 100 : precoUSD;         // tarifa subiu
		return { id: it.id, precoUSD: novoUSD, precoBRL: Math.round(novoUSD * usdBrl * 100) / 100, disponivel: true, motivo: null };
	});

	return {
		itens,
		modo: "completo", // o BE mantem o campo, sempre "completo", para nao quebrar contrato no futuro
		cambio: { usdBrl, atualizadoEm: agora.toISOString(), validoAte: validoAte.toISOString(), congelado: false },
	};
};
